'use client';

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ProjectModal from '@/components/ui/ProjectModal';
import { projectsData, Project } from '@/data/projects';
import { getTechIcon } from '@/lib/utils/techIcons';
import {
  Sparkles,
  Blocks,
  Barcode,
  HousePlug,
  CarFront,
  Stethoscope,
  ChartLine,
  ExternalLink,
} from 'lucide-react';
import Image from 'next/image';

export default function Projects() {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Animation variants for staggered card entrance
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 50,
      scale: 0.9,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
      },
    },
    hover: {
      y: -8,
      scale: 1.02,
      transition: {
        duration: 0.3,
      },
    },
  };

  const iconVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.5,
        delay: 0.3,
      },
    },
    hover: {
      scale: 1.1,
      rotate: 5,
      transition: {
        duration: 0.2,
      },
    },
  };

  const patternVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 0.2,
      scale: 1,
      transition: {
        duration: 0.8,
        delay: 0.4,
      },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: 0.5,
      },
    },
  };

  // Helper function to render Lucide React icons
  const renderIcon = (iconName: string) => {
    const iconMap: Record<
      string,
      React.ComponentType<{ className?: string }>
    > = {
      Sparkles: Sparkles,
      Blocks: Blocks,
      Barcode: Barcode,
      HousePlug: HousePlug,
      CarFront: CarFront,
      Stethoscope: Stethoscope,
      ChartLine: ChartLine,
    };

    const IconComponent = iconMap[iconName];
    return IconComponent ? (
      <IconComponent className="h-12 w-12 text-white" />
    ) : (
      <Sparkles className="h-8 w-8 text-white" />
    );
  };

  // Function to render different pattern types
  const renderPattern = (pattern: Project['pattern']) => {
    switch (pattern) {
      case 'circles':
        return (
          <motion.div
            className="absolute inset-0 opacity-20"
            variants={patternVariants}
          >
            <div
              className="absolute top-4 left-4 h-3 w-3 animate-spin rounded-full bg-purple-400"
              style={{ animationDuration: '3s' }}
            ></div>
            <div
              className="absolute top-12 right-8 h-2 w-2 animate-bounce rounded-full bg-purple-300"
              style={{ animationDelay: '0.5s', animationDuration: '1s' }}
            ></div>
            <div
              className="absolute bottom-8 left-12 h-4 w-4 animate-ping rounded-full bg-purple-400"
              style={{ animationDelay: '1s', animationDuration: '2s' }}
            ></div>
            <div
              className="absolute right-4 bottom-16 h-2 w-2 animate-bounce rounded-full bg-purple-300"
              style={{ animationDelay: '1.5s', animationDuration: '0.8s' }}
            ></div>
            <div
              className="absolute top-20 left-20 h-1 w-1 animate-spin rounded-full bg-pink-400"
              style={{ animationDelay: '2s', animationDuration: '4s' }}
            ></div>
            <div
              className="absolute top-32 right-16 h-2 w-2 animate-bounce rounded-full bg-blue-300"
              style={{ animationDelay: '2.5s', animationDuration: '1.2s' }}
            ></div>
          </motion.div>
        );

      case 'grid':
        return (
          <motion.div
            className="absolute inset-0 opacity-20"
            variants={patternVariants}
          >
            <svg
              className="h-full w-full animate-pulse"
              style={{ animationDuration: '2s' }}
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <pattern
                  id="grid"
                  x="0"
                  y="0"
                  width="40"
                  height="40"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 40 0 L 0 0 0 40"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    className="text-slate-400"
                  />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </motion.div>
        );

      case 'dots':
        return (
          <motion.div
            className="absolute inset-0 opacity-20"
            variants={patternVariants}
          >
            <svg
              className="h-full w-full animate-pulse"
              style={{ animationDuration: '1.5s' }}
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <pattern
                  id="dots"
                  x="0"
                  y="0"
                  width="20"
                  height="20"
                  patternUnits="userSpaceOnUse"
                >
                  <circle
                    cx="10"
                    cy="10"
                    r="1"
                    fill="currentColor"
                    className="text-green-400"
                  />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#dots)" />
            </svg>
          </motion.div>
        );

      case 'waves':
        return (
          <motion.div
            className="absolute inset-0 opacity-20"
            variants={patternVariants}
          >
            <svg
              className="h-full w-full animate-pulse"
              style={{ animationDuration: '3s' }}
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <pattern
                  id="waves"
                  x="0"
                  y="0"
                  width="60"
                  height="60"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 0 30 Q 15 20 30 30 T 60 30"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    className="text-orange-400"
                  />
                  <path
                    d="M 0 40 Q 15 30 30 40 T 60 40"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    className="text-red-400"
                  />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#waves)" />
            </svg>
          </motion.div>
        );

      case 'hexagons':
        return (
          <motion.div
            className="absolute inset-0 opacity-20"
            variants={patternVariants}
          >
            <svg
              className="h-full w-full animate-pulse"
              style={{ animationDuration: '2.5s' }}
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <pattern
                  id="hexagons"
                  x="0"
                  y="0"
                  width="50"
                  height="50"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 25 5 L 45 15 L 45 35 L 25 45 L 5 35 L 5 15 Z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    className="text-purple-400"
                  />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#hexagons)" />
            </svg>
          </motion.div>
        );

      case 'stars':
        return (
          <motion.div
            className="absolute inset-0 opacity-20"
            variants={patternVariants}
          >
            <div
              className="absolute top-6 left-6 animate-spin text-yellow-400"
              style={{ animationDuration: '4s' }}
            >
              ★
            </div>
            <div
              className="absolute top-16 right-12 animate-bounce text-blue-400"
              style={{ animationDelay: '0.5s', animationDuration: '0.8s' }}
            >
              ★
            </div>
            <div
              className="absolute bottom-12 left-16 animate-ping text-cyan-400"
              style={{ animationDelay: '1s', animationDuration: '2s' }}
            >
              ★
            </div>
            <div
              className="absolute right-8 bottom-20 animate-bounce text-indigo-400"
              style={{ animationDelay: '1.5s', animationDuration: '1.2s' }}
            >
              ★
            </div>
            <div
              className="absolute top-24 left-32 animate-spin text-purple-400"
              style={{ animationDuration: '6s' }}
            >
              ★
            </div>
            <div
              className="absolute top-36 right-24 animate-bounce text-pink-400"
              style={{ animationDelay: '2.5s', animationDuration: '0.9s' }}
            >
              ★
            </div>
          </motion.div>
        );

      default:
        return (
          <motion.div
            className="absolute inset-0 opacity-20"
            variants={patternVariants}
          >
            <div
              className="absolute top-4 left-4 h-3 w-3 animate-spin rounded-full bg-purple-400"
              style={{ animationDuration: '3s' }}
            ></div>
            <div
              className="absolute top-12 right-8 h-2 w-2 animate-bounce rounded-full bg-purple-300"
              style={{ animationDelay: '0.5s', animationDuration: '0.8s' }}
            ></div>
          </motion.div>
        );
    }
  };

  const handleProjectClick = (project: Project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  return (
    <div className="relative">
      <div className="w-full">
        <div className="min-h-screen">
          <main>
            <div className="pb-20">
              <div className="mx-auto max-w-7xl px-4">
                {/* Project Grid */}
                <motion.div
                  className="grid gap-8 md:grid-cols-2 lg:grid-cols-3"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {projectsData.map((project, index) => (
                    <motion.div
                      key={project.id}
                      className="group transform:none relative cursor-pointer"
                      onClick={() => handleProjectClick(project)}
                      variants={cardVariants}
                      whileHover="hover"
                      custom={index}
                    >
                      {/* Dark-themed Project Card */}
                      <div className="group relative overflow-hidden rounded-xl border border-slate-700/50 bg-gradient-to-br from-slate-900/20 via-slate-800/20 to-slate-900/20 p-6 text-white shadow-2xl backdrop-blur-sm transition-all duration-300 hover:border-purple-500/30 hover:shadow-purple-500/20">
                        {/* Top accent bar for visual separation */}
                        <motion.div
                          className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500 opacity-30 transition-opacity duration-300 group-hover:opacity-100"
                          initial={{ scaleX: 0 }}
                          animate={{ scaleX: 1 }}
                          transition={{ delay: 0.6, duration: 0.5 }}
                        ></motion.div>

                        {/* Top Visual Section with AI Icon and Buttons */}
                        <div
                          className={`relative h-48 bg-gradient-to-br ${project.gradient.from} ${project.gradient.to} mb-6 rounded-xl`}
                        >
                          {/* Dynamic Pattern Based on Project Type */}
                          {renderPattern(project.pattern)}

                          {/* AI Icon */}
                          <motion.div
                            className="relative z-10 flex h-full flex-col items-center justify-center"
                            variants={iconVariants}
                          >
                            <motion.div
                              className={`mb-4 flex items-center justify-center rounded-3xl bg-gradient-to-br ${project.iconBackground.from} ${project.iconBackground.to} p-6 shadow-lg`}
                              whileHover={{
                                scale: 1.1,
                                rotate: 5,
                                transition: { duration: 0.2 },
                              }}
                            >
                              {renderIcon(project.icon)}
                            </motion.div>

                            {/* Interactive Buttons */}
                            <motion.div
                              className="flex gap-3"
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.8, duration: 0.4 }}
                            >
                              <button
                                className={`rounded-full border border-slate-500/30 bg-slate-500/20 px-4 py-2 text-sm font-medium transition-colors duration-200 hover:bg-slate-700/80 ${project.textColor}`}
                                aria-label={`Project type: ${project.type}`}
                              >
                                {project.type}
                              </button>
                              <button
                                className={`rounded-full border border-slate-500/30 bg-slate-500/20 px-4 py-2 text-sm font-medium transition-colors duration-200 hover:bg-slate-700/80 ${project.textColor}`}
                                aria-label={`Project category: ${project.category}`}
                              >
                                {project.category}
                              </button>
                            </motion.div>
                          </motion.div>
                        </div>

                        {/* Project Content */}
                        <motion.div
                          className="group pb-6"
                          variants={contentVariants}
                        >
                          {/* Project Title */}
                          <motion.div
                            className="bricolage mb-3 font-serif text-xl font-bold text-white transition-colors duration-200 group-hover:text-blue-400"
                            whileHover={{ color: '#60A5FA' }}
                          >
                            {project.title}
                          </motion.div>

                          {/* Project Description */}
                          <motion.p
                            className="mb-4 line-clamp-2 font-sans text-lg leading-relaxed text-slate-300"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.9, duration: 0.5 }}
                          >
                            {project.shortDescription}
                          </motion.p>

                          {/* Technologies */}
                          <motion.div
                            className="mb-6"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.0, duration: 0.5 }}
                          >
                            <div className="flex flex-wrap gap-3">
                              {project.technologies
                                .slice(0, 4)
                                .map((tech, techIndex) => {
                                  const techIcon = getTechIcon(tech);
                                  return (
                                    <motion.span
                                      key={tech}
                                      className="flex items-center gap-2 rounded-full border border-indigo-500/10 bg-indigo-600/20 px-3 py-2 font-mono text-xs font-medium text-blue-400 shadow-sm transition-all duration-200 hover:scale-105 hover:bg-blue-500/50"
                                      initial={{ opacity: 0, scale: 0.8 }}
                                      animate={{ opacity: 1, scale: 1 }}
                                      transition={{
                                        delay: 1.1 + techIndex * 0.1,
                                        duration: 0.3,
                                        ease: 'backOut',
                                      }}
                                      whileHover={{
                                        scale: 1.05,
                                        y: -2,
                                        transition: { duration: 0.2 },
                                      }}
                                    >
                                      {techIcon ? (
                                        <Image
                                          width={16}
                                          height={16}
                                          src={techIcon}
                                          alt={`${tech} icon`}
                                          className="h-4 w-4"
                                          onError={e => {
                                            // Hide the image if it fails to load
                                            (
                                              e.target as HTMLImageElement
                                            ).style.display = 'none';
                                          }}
                                        />
                                      ) : (
                                        <div className="flex h-4 w-4 items-center justify-center rounded-full border border-blue-500/30 bg-blue-500/20">
                                          <span className="text-[8px] font-bold text-blue-400">
                                            {tech.charAt(0).toUpperCase()}
                                          </span>
                                        </div>
                                      )}
                                      {tech}
                                    </motion.span>
                                  );
                                })}
                              {project.technologies.length > 4 && (
                                <motion.span
                                  className="rounded-full border border-indigo-600/10 bg-indigo-800/20 px-3 py-1 font-mono text-xs font-medium text-blue-200 shadow-sm transition-all duration-200 hover:scale-105 hover:bg-blue-500/50"
                                  initial={{ opacity: 0, scale: 0.8 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ delay: 1.5, duration: 0.3 }}
                                  whileHover={{ scale: 1.05 }}
                                >
                                  +{project.technologies.length - 4} more
                                </motion.span>
                              )}
                            </div>
                          </motion.div>

                          {/* View Details Link */}
                          <motion.div
                            className="flex items-center justify-between"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 1.2, duration: 0.4 }}
                          >
                            <motion.button
                              onClick={e => {
                                e.stopPropagation();
                                handleProjectClick(project);
                              }}
                              aria-label={`View details for ${project.title} project`}
                              className="flex cursor-pointer items-center gap-2 text-sm font-medium text-blue-400 transition-colors duration-200 group-hover:text-blue-200"
                              whileHover={{
                                x: 5,
                                color: '#93C5FD',
                                transition: { duration: 0.2 },
                              }}
                            >
                              View Details
                              <motion.div
                                whileHover={{ rotate: 45 }}
                                transition={{ duration: 0.2 }}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </motion.div>
                            </motion.button>
                          </motion.div>
                        </motion.div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Reusable Modal */}
      {selectedProject && (
        <ProjectModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          project={selectedProject}
        />
      )}
    </div>
  );
}
